import React from "react";
import {Box, Button, Typography} from "@mui/material";
import {
    DescriptionOutlined, SchoolOutlined, ReportProblemOutlined,
    Diversity3Outlined, DesignServicesOutlined,
    AutoAwesome, Insights, NotificationsActive, AccountTree,
    ArrowForward, Bolt, Lock, RocketLaunch, VerifiedUserOutlined,
    GavelOutlined, FactCheckOutlined, Timeline, Speed,
    AssignmentTurnedInOutlined, Visibility, HowToReg, HistoryEdu,
    CheckCircleOutlined, Psychology, SearchOutlined,
    EditNoteOutlined, BuildCircleOutlined, AssignmentOutlined,
    ManageSearchOutlined, MonitorHeartOutlined, UploadFileOutlined,
    QueryStatsOutlined, SmartToyOutlined, ShieldOutlined,
    AccountBalanceOutlined, Hub,
} from "@mui/icons-material";
import billLogo from "../assets/bill-logo.png";
import EventBus from "../util/EventBus";
import useAppContext from "../util/AppContext";
import {SessionContext} from "../util/SessionContext";
import {Blue1, Blue3, Blue4, Blue5, Green4, Grey1b, Grey2, Grey5, Grey6, Grey7, Grey17, White} from "../theme/styles";
import parkourLogo from "../assets/ParkourLogo.svg";

type Pillar = {icon: React.ReactNode; title: string; description: string};
type Differentiator = {icon: React.ReactNode; eyebrow: string; title: string; description: string};

export default function Home() {
    const sessionContext: SessionContext | undefined = useAppContext()?.sessionContext;

    const openLogin = () => {
        sessionContext?.setShowLoginDialog?.(true);
        EventBus.session.emit("update");
    };

    const scrollToModules = () => {
        document.getElementById("modules")?.scrollIntoView({behavior: "smooth", block: "start"});
    };

    const pillars: Pillar[] = [
        {icon: <DescriptionOutlined sx={{fontSize: 36, color: Blue3}}/>, title: "Document Control",
            description: "Versioned SOPs, work instructions, and controlled forms with electronic signatures, effective-date routing, and full revision history."},
        {icon: <SchoolOutlined sx={{fontSize: 36, color: Blue3}}/>, title: "Training Management",
            description: "Role-based training matrices and automatic re-training on every controlled-document revision — with signed completion records."},
        {icon: <ReportProblemOutlined sx={{fontSize: 36, color: Blue3}}/>, title: "Quality Events",
            description: "CAPAs, deviations, complaints, and non-conformances on a single investigation backbone — root cause, actions, and effectiveness checks."},
        {icon: <Diversity3Outlined sx={{fontSize: 36, color: Blue3}}/>, title: "Supplier Quality",
            description: "Approved supplier list, qualification workflows, performance scorecards, and supplier corrective action requests in one place."},
        {icon: <DesignServicesOutlined sx={{fontSize: 36, color: Blue3}}/>, title: "Design Controls",
            description: "Design History Files, requirements traceability, design reviews, and ISO 14971 risk management from concept to launch."},
    ];

    const differentiators: Differentiator[] = [
        {icon: <Speed sx={{fontSize: 32, color: Blue3}}/>, eyebrow: "Live in 60 days",
            title: "Audit-ready from day one",
            description: "A pre-validated configuration, audit-tested templates, and dedicated onboarding put your validation package in the auditor's hands in under two months."},
        {icon: <AutoAwesome sx={{fontSize: 32, color: Blue3}}/>, eyebrow: "Easiest eQMS to use",
            title: "Designed for the whole organization",
            description: "Built for the engineer who logs in once a month and the quality lead who lives in the system — without the Salesforce-style complexity that stalls adoption."},
        {icon: <NotificationsActive sx={{fontSize: 32, color: Blue3}}/>, eyebrow: "Always-on regulatory intelligence",
            title: "Regulatory agents run continuously",
            description: "Background agents watch FDA, ISO, and EU regulatory feeds, flag impacted procedures, and propose updates before your next inspection."},
    ];

    const standards = [
        "FDA 21 CFR Part 11",
        "FDA 21 CFR Part 820",
        "ISO 13485",
        "ISO 14971",
        "EU MDR",
        "EU IVDR",
        "ICH Q9 / Q10",
        "GxP",
        "GDPR",
        "HIPAA",
    ];

    return (
        <div style={{display: "flex", flexDirection: "column", backgroundColor: Grey1b, width: "100%"}}>

            {/* ───────────── Hero ───────────── */}
            <Box sx={{
                position: "relative",
                background: `linear-gradient(135deg, ${Blue5} 0%, ${Blue4} 45%, ${Blue3} 100%)`,
                color: White,
                overflow: "hidden",
                px: {xs: 4, md: 10},
                py: {xs: 8, md: 12},
            }}>
                {/* Decorative orbiting icon ring */}
                <Box sx={{
                    position: "absolute",
                    right: {xs: -120, md: -60},
                    top: "50%",
                    transform: "translateY(-50%)",
                    width: 520,
                    height: 520,
                    borderRadius: "50%",
                    border: `1px solid rgba(255,255,255,0.18)`,
                    display: {xs: "none", md: "block"},
                    pointerEvents: "none",
                }}>
                    <Box sx={{position: "absolute", inset: 40, borderRadius: "50%", border: `1px solid rgba(255,255,255,0.12)`}}/>
                    <Box sx={{position: "absolute", inset: 90, borderRadius: "50%", border: `1px solid rgba(255,255,255,0.08)`}}/>
                    {[
                        {Icon: DescriptionOutlined, top: "5%", left: "45%"},
                        {Icon: SchoolOutlined, top: "30%", left: "92%"},
                        {Icon: ReportProblemOutlined, top: "75%", left: "85%"},
                        {Icon: Diversity3Outlined, top: "85%", left: "20%"},
                        {Icon: DesignServicesOutlined, top: "30%", left: "-5%"},
                    ].map(({Icon, top, left}, i) => (
                        <Box key={i} sx={{
                            position: "absolute", top, left, transform: "translate(-50%, -50%)",
                            backgroundColor: "rgba(255,255,255,0.12)", backdropFilter: "blur(6px)",
                            borderRadius: "16px", width: 72, height: 72,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            border: "1px solid rgba(255,255,255,0.25)",
                        }}>
                            <Icon sx={{fontSize: 36, color: White}}/>
                        </Box>
                    ))}
                </Box>

                <Box sx={{position: "relative", maxWidth: 760, zIndex: 1}}>
                    <Box sx={{display: "flex", alignItems: "center", gap: 2, mb: 3}}>
                        <img src={parkourLogo} alt="Parkour" style={{height: 38, filter: "brightness(0) invert(1)"}}/>
                        <Box sx={{height: 24, width: "1px", backgroundColor: "rgba(255,255,255,0.4)"}}/>
                        <Typography sx={{fontSize: 18, fontWeight: 500, letterSpacing: "0.5px"}}>
                            Quality Management System
                        </Typography>
                    </Box>
                    <Typography sx={{fontSize: {xs: 36, md: 56}, fontWeight: 700, lineHeight: 1.1, mb: 3}}>
                        The eQMS built for FDA-regulated companies
                    </Typography>
                    <Typography sx={{fontSize: {xs: 16, md: 20}, lineHeight: 1.6, color: "rgba(255,255,255,0.92)", mb: 5, maxWidth: 640}}>
                        Document control, training, quality events, supplier quality, and design controls —
                        unified in one validated platform that medical device, pharma, and biotech teams
                        can stand up in 60 days.
                    </Typography>
                    <Box sx={{display: "flex", flexWrap: "wrap", gap: 2}}>
                        <Button
                            variant="contained"
                            size="large"
                            onClick={openLogin}
                            endIcon={<ArrowForward/>}
                            sx={{
                                backgroundColor: White, color: Blue4, fontWeight: 600, px: 4, py: 1.4,
                                textTransform: "none", fontSize: 16,
                                "&:hover": {backgroundColor: Blue1, color: Blue5},
                            }}
                        >
                            Sign in
                        </Button>
                        {/* WHAT IT DOES AND WHAT IT DOES NOT, on its own page (Ed, 2026-10-31: "a
                            capabilities page like t4 has"): every capability with a status, a worked
                            example for each one that is built, and the bill of materials beside six
                            other products. A plain href: the page is readable signed out. */}
                        <Button
                            variant="outlined"
                            size="large"
                            href="/capabilities"
                            sx={{
                                color: White, borderColor: "rgba(255,255,255,0.6)",
                                fontWeight: 500, px: 4, py: 1.4, textTransform: "none", fontSize: 16,
                                "&:hover": {borderColor: White, backgroundColor: "rgba(255,255,255,0.08)"},
                            }}
                        >
                            See everything it does
                        </Button>
                        <Button
                            variant="outlined"
                            size="large"
                            onClick={scrollToModules}
                            startIcon={<RocketLaunch/>}
                            sx={{
                                color: White, borderColor: "rgba(255,255,255,0.6)",
                                fontWeight: 500, px: 4, py: 1.4, textTransform: "none", fontSize: 16,
                                "&:hover": {borderColor: White, backgroundColor: "rgba(255,255,255,0.08)"},
                            }}
                        >
                            See how it works
                        </Button>
                    </Box>

                    <Box sx={{display: "flex", flexWrap: "wrap", gap: 4, mt: 6, color: "rgba(255,255,255,0.85)"}}>
                        {[
                            {icon: <Lock sx={{fontSize: 18}}/>, label: "FDA 21 CFR Part 11 ready"},
                            {icon: <VerifiedUserOutlined sx={{fontSize: 18}}/>, label: "ISO 13485 & EU MDR aligned"},
                            {icon: <Bolt sx={{fontSize: 18}}/>, label: "Validation package included"},
                            {icon: <RocketLaunch sx={{fontSize: 18}}/>, label: "Live in 60 days"},
                        ].map((b, i) => (
                            <Box key={i} sx={{display: "flex", alignItems: "center", gap: 1, fontSize: 14}}>
                                {b.icon}<span>{b.label}</span>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>

            {/* ───────────── Module pillars ───────────── */}
            <Box id="modules" sx={{px: {xs: 4, md: 10}, py: {xs: 8, md: 10}, backgroundColor: White}}>
                <Box sx={{textAlign: "center", mb: 6, maxWidth: 800, mx: "auto"}}>
                    <Typography sx={{fontSize: 14, fontWeight: 600, color: Blue3, textTransform: "uppercase", letterSpacing: "1.5px", mb: 1.5}}>
                        One unified platform
                    </Typography>
                    <Typography sx={{fontSize: {xs: 28, md: 36}, fontWeight: 700, color: Grey17, lineHeight: 1.2, mb: 2}}>
                        Every quality process, on a single traceable backbone
                    </Typography>
                    <Typography sx={{fontSize: 16, color: Grey7, lineHeight: 1.6}}>
                        Retire the patchwork of shared drives, spreadsheets, and email approvals.
                        Five purpose-built modules speak the same data model and produce one
                        audit trail.
                    </Typography>
                </Box>

                <Box sx={{
                    display: "grid",
                    gridTemplateColumns: {xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr", lg: "repeat(5, 1fr)"},
                    gap: 3,
                }}>
                    {pillars.map((p, i) => (
                        <Box key={i} sx={{
                            backgroundColor: Grey1b,
                            border: `1px solid ${Grey2}`,
                            borderRadius: "12px",
                            p: 3,
                            transition: "transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease",
                            "&:hover": {transform: "translateY(-4px)", boxShadow: "0 12px 30px rgba(20, 152, 216, 0.15)", borderColor: Blue3},
                        }}>
                            <Box sx={{
                                width: 56, height: 56, borderRadius: "12px",
                                backgroundColor: "rgba(20, 152, 216, 0.10)",
                                display: "flex", alignItems: "center", justifyContent: "center", mb: 2,
                            }}>
                                {p.icon}
                            </Box>
                            <Typography sx={{fontSize: 18, fontWeight: 600, color: Grey17, mb: 1}}>{p.title}</Typography>
                            <Typography sx={{fontSize: 14, color: Grey7, lineHeight: 1.55}}>{p.description}</Typography>
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* ───────────── Differentiators (Qualio themes) ───────────── */}
            <Box sx={{px: {xs: 4, md: 10}, py: {xs: 8, md: 10}, backgroundColor: Grey1b}}>
                <Box sx={{textAlign: "center", mb: 6, maxWidth: 760, mx: "auto"}}>
                    <Typography sx={{fontSize: 14, fontWeight: 600, color: Blue3, textTransform: "uppercase", letterSpacing: "1.5px", mb: 1.5}}>
                        Why life-sciences teams pick us
                    </Typography>
                    <Typography sx={{fontSize: {xs: 28, md: 36}, fontWeight: 700, color: Grey17, lineHeight: 1.2}}>
                        Fast to launch. Easy to live in. Always watching the regs.
                    </Typography>
                </Box>
                <Box sx={{
                    display: "grid",
                    gridTemplateColumns: {xs: "1fr", md: "repeat(3, 1fr)"},
                    gap: 3,
                }}>
                    {differentiators.map((d, i) => (
                        <Box key={i} sx={{
                            backgroundColor: White,
                            border: `1px solid ${Grey2}`,
                            borderLeft: `4px solid ${Blue3}`,
                            borderRadius: "10px",
                            p: 3.5,
                            transition: "box-shadow 200ms ease",
                            "&:hover": {boxShadow: "0 8px 24px rgba(0,0,0,0.06)"},
                        }}>
                            <Box sx={{display: "flex", alignItems: "center", gap: 1.5, mb: 1.5}}>
                                <Box sx={{
                                    width: 48, height: 48, borderRadius: "10px",
                                    backgroundColor: "rgba(20, 152, 216, 0.10)",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                }}>
                                    {d.icon}
                                </Box>
                                <Typography sx={{fontSize: 12, fontWeight: 700, color: Blue4, textTransform: "uppercase", letterSpacing: "1px"}}>
                                    {d.eyebrow}
                                </Typography>
                            </Box>
                            <Typography sx={{fontSize: 19, fontWeight: 600, color: Grey17, mb: 1, lineHeight: 1.3}}>
                                {d.title}
                            </Typography>
                            <Typography sx={{fontSize: 14, color: Grey7, lineHeight: 1.6}}>
                                {d.description}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* ───────────── Feature presentation (everything the QMS does) ───────────── */}
            <Box sx={{px: {xs: 4, md: 10}, py: {xs: 8, md: 10}, backgroundColor: White}}>
                <Box sx={{textAlign: "center", mb: 7, maxWidth: 820, mx: "auto"}}>
                    <Typography sx={{fontSize: 14, fontWeight: 600, color: Blue3, textTransform: "uppercase", letterSpacing: "1.5px", mb: 1.5}}>
                        Everything your quality system needs
                    </Typography>
                    <Typography sx={{fontSize: {xs: 28, md: 36}, fontWeight: 700, color: Grey17, lineHeight: 1.2, mb: 2}}>
                        A complete feature set, not a thin SaaS wrapper
                    </Typography>
                    <Typography sx={{fontSize: 16, color: Grey7, lineHeight: 1.6}}>
                        Every workflow a life-sciences quality team needs to operate &mdash; from authoring
                        the first SOP to monitoring post-market adverse events &mdash; lives inside one
                        validated platform with one audit trail.
                    </Typography>
                </Box>

                {([
                    {
                        eyebrow: "Quality Operations",
                        sub: "The day-to-day backbone of a regulated organization.",
                        features: [
                            {icon: <EditNoteOutlined sx={{fontSize: 28, color: Blue3}}/>, title: "Document Management",
                                body: "Version-controlled SOPs, work instructions, forms, and policies with native authoring, live collaboration, e-signature routing, and effective-date control."},
                            {icon: <SchoolOutlined sx={{fontSize: 28, color: Blue3}}/>, title: "Training Management",
                                body: "Role-based curricula assigned automatically. Every controlled-document revision triggers re-training and tracks signed completion records."},
                            {icon: <ReportProblemOutlined sx={{fontSize: 28, color: Blue3}}/>, title: "Quality Event Management",
                                body: "Configurable workflows for deviations, non-conformances, and incidents — with routed approvals, e-signatures, and zero tasks falling through the cracks."},
                            {icon: <BuildCircleOutlined sx={{fontSize: 28, color: Blue3}}/>, title: "CAPA Management",
                                body: "Quality events flow directly into corrective and preventive action — root cause, action plans, effectiveness checks, all on the same record."},
                            {icon: <AccountTree sx={{fontSize: 28, color: Blue3}}/>, title: "Change Control Management",
                                body: "Configurable change workflows with built-in electronic signatures, impact assessment, and full traceability to affected artifacts."},
                        ],
                    },
                    {
                        eyebrow: "Lifecycle & Risk",
                        sub: "Build the product right, qualify the people and parts behind it.",
                        features: [
                            {icon: <DesignServicesOutlined sx={{fontSize: 28, color: Blue3}}/>, title: "Design Control Management",
                                body: "Design History Files, requirements traceability, design reviews, and verification/validation evidence — purpose-built for medical device development."},
                            {icon: <ShieldOutlined sx={{fontSize: 28, color: Blue3}}/>, title: "Risk Management",
                                body: "ISO 14971-aligned hazard analyses, FMEAs, and risk control traceability that move with your design baseline."},
                            {icon: <Diversity3Outlined sx={{fontSize: 28, color: Blue3}}/>, title: "Supplier Management",
                                body: "Approved supplier list, qualification workflows, supplier scorecards, and supplier corrective action requests — all linked to incoming material."},
                            {icon: <FactCheckOutlined sx={{fontSize: 28, color: Blue3}}/>, title: "Audit Management",
                                body: "Internal, external, and regulatory inspections — organized documentation, observations, responses, and traceability ready on inspection day."},
                        ],
                    },
                    {
                        eyebrow: "Post-Market & Submission",
                        sub: "Stay accountable after the product ships.",
                        features: [
                            {icon: <MonitorHeartOutlined sx={{fontSize: 28, color: Blue3}}/>, title: "Pharmacovigilance & PMS",
                                body: "Adverse event capture, MDR/MIR decisioning, periodic safety updates, and post-market surveillance reports — wired into complaints and CAPAs."},
                            {icon: <AssignmentOutlined sx={{fontSize: 28, color: Blue3}}/>, title: "Complaint Handling",
                                body: "Intake from any channel, regulatory-decision logic, investigations, and trend reporting on a single complaint record."},
                            {icon: <UploadFileOutlined sx={{fontSize: 28, color: Blue3}}/>, title: "Regulatory Submission",
                                body: "Assemble 510(k), De Novo, PMA, IND, and CE-marking dossiers from controlled records — no last-minute scramble to find evidence."},
                        ],
                    },
                    {
                        eyebrow: "Intelligence & Insight",
                        sub: "Spot the trend, the gap, and the next finding before the auditor does.",
                        features: [
                            {icon: <SmartToyOutlined sx={{fontSize: 28, color: Blue3}}/>, title: "AI Automation",
                                body: "Automates routine quality tasks — drafting investigations, classifying complaints, summarizing reviews — with every action logged for audit."},
                            {icon: <ManageSearchOutlined sx={{fontSize: 28, color: Blue3}}/>, title: "Compliance Intelligence Agent",
                                body: "Continuously monitors your live quality system against FDA, ISO, and GxP frameworks and flags audit-readiness gaps before they become findings."},
                            {icon: <QueryStatsOutlined sx={{fontSize: 28, color: Blue3}}/>, title: "Quality Analytics",
                                body: "Cross-module dashboards on event aging, supplier performance, training compliance, and CAPA effectiveness — exportable for management review."},
                        ],
                    },
                ] as {eyebrow: string; sub: string; features: {icon: React.ReactNode; title: string; body: string}[]}[]).map((group, gi) => (
                    <Box key={gi} sx={{maxWidth: 1280, mx: "auto", mb: gi === 3 ? 0 : 6}}>
                        <Box sx={{display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 2.5}}>
                            <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                                <Box sx={{width: 6, height: 26, borderRadius: "3px", backgroundColor: Blue3}}/>
                                <Typography sx={{fontSize: 20, fontWeight: 700, color: Grey17, letterSpacing: "-0.25px"}}>
                                    {group.eyebrow}
                                </Typography>
                            </Box>
                            <Typography sx={{fontSize: 14, color: Grey6, fontStyle: "italic"}}>
                                {group.sub}
                            </Typography>
                        </Box>
                        <Box sx={{
                            display: "grid",
                            gridTemplateColumns: {xs: "1fr", sm: "1fr 1fr", lg: "repeat(3, 1fr)", xl: "repeat(5, 1fr)"},
                            gap: 2.5,
                        }}>
                            {group.features.map((f, fi) => (
                                <Box key={fi} sx={{
                                    backgroundColor: Grey1b,
                                    border: `1px solid ${Grey2}`,
                                    borderRadius: "12px",
                                    p: 2.75,
                                    display: "flex", flexDirection: "column",
                                    transition: "border-color 200ms ease, box-shadow 200ms ease, transform 200ms ease",
                                    "&:hover": {borderColor: Blue3, boxShadow: "0 10px 24px rgba(20,152,216,0.10)", transform: "translateY(-2px)"},
                                }}>
                                    <Box sx={{
                                        width: 48, height: 48, borderRadius: "10px",
                                        backgroundColor: "rgba(20,152,216,0.10)",
                                        display: "flex", alignItems: "center", justifyContent: "center", mb: 1.5,
                                    }}>
                                        {f.icon}
                                    </Box>
                                    <Typography sx={{fontSize: 16, fontWeight: 700, color: Grey17, mb: 0.75, lineHeight: 1.3}}>
                                        {f.title}
                                    </Typography>
                                    <Typography sx={{fontSize: 13.5, color: Grey7, lineHeight: 1.55}}>
                                        {f.body}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                ))}
            </Box>

            {/* ───────────── Regulatory standards row ───────────── */}
            <Box sx={{px: {xs: 4, md: 10}, py: {xs: 8, md: 10}, backgroundColor: White}}>
                <Box sx={{textAlign: "center", mb: 5, maxWidth: 820, mx: "auto"}}>
                    <Typography sx={{fontSize: 14, fontWeight: 600, color: Blue3, textTransform: "uppercase", letterSpacing: "1.5px", mb: 1.5}}>
                        Built around the regs
                    </Typography>
                    <Typography sx={{fontSize: {xs: 28, md: 36}, fontWeight: 700, color: Grey17, lineHeight: 1.2, mb: 2}}>
                        Pre-validated for the standards your auditors care about
                    </Typography>
                    <Typography sx={{fontSize: 16, color: Grey7, lineHeight: 1.6}}>
                        Every workflow ships with mapped controls and pre-baked evidence — no
                        more wiring controls into a generic compliance tool.
                    </Typography>
                </Box>
                <Box sx={{
                    display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 1.5,
                    maxWidth: 1100, mx: "auto",
                }}>
                    {standards.map((s, i) => (
                        <Box key={i} sx={{
                            display: "inline-flex", alignItems: "center", gap: 1,
                            backgroundColor: Grey1b,
                            border: `1px solid ${Grey2}`,
                            borderRadius: "999px",
                            px: 2.25, py: 1,
                            transition: "border-color 200ms ease, background-color 200ms ease",
                            "&:hover": {borderColor: Blue3, backgroundColor: "rgba(20,152,216,0.06)"},
                        }}>
                            <GavelOutlined sx={{fontSize: 16, color: Blue4}}/>
                            <Typography sx={{fontSize: 14, fontWeight: 600, color: Grey17}}>
                                {s}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* ───────────── Integrations (most comprehensive in market) ───────────── */}
            <Box sx={{px: {xs: 4, md: 10}, py: {xs: 8, md: 10}, backgroundColor: Grey1b}}>
                <Box sx={{textAlign: "center", mb: 6, maxWidth: 880, mx: "auto"}}>
                    <Box sx={{
                        display: "inline-flex", alignItems: "center", gap: 1,
                        backgroundColor: "rgba(20,152,216,0.10)",
                        border: `1px solid rgba(20,152,216,0.30)`,
                        borderRadius: "999px",
                        px: 2, py: 0.6, mb: 2.5,
                    }}>
                        <Hub sx={{fontSize: 16, color: Blue4}}/>
                        <Typography sx={{fontSize: 12, fontWeight: 700, color: Blue4, textTransform: "uppercase", letterSpacing: "1px"}}>
                            Most comprehensive integration suite in the market
                        </Typography>
                    </Box>
                    <Typography sx={{fontSize: {xs: 28, md: 36}, fontWeight: 700, color: Grey17, lineHeight: 1.2, mb: 2}}>
                        Connect every system that runs your regulated operation
                    </Typography>
                    <Typography sx={{fontSize: 16, color: Grey7, lineHeight: 1.6}}>
                        Identity, productivity, ticketing, engineering, finance, and signatures &mdash; out of the
                        box. No other eQMS ships with this much pre-built connectivity, and everything not on the
                        list is one REST call away.
                    </Typography>
                </Box>

                {/* Stat strip */}
                <Box sx={{
                    display: "grid",
                    gridTemplateColumns: {xs: "1fr 1fr", md: "repeat(4, 1fr)"},
                    gap: 2.5,
                    maxWidth: 1100, mx: "auto",
                    mb: 6,
                }}>
                    {[
                        {value: "60+", label: "Pre-built integrations"},
                        {value: "6", label: "Integration categories"},
                        {value: "100%", label: "API-covered records"},
                        {value: "0", label: "Engineering days to enable SSO"},
                    ].map((s, i) => (
                        <Box key={i} sx={{
                            backgroundColor: White,
                            border: `1px solid ${Grey2}`,
                            borderRadius: "12px",
                            p: 2.5,
                            textAlign: "center",
                        }}>
                            <Typography sx={{fontSize: 32, fontWeight: 800, color: Blue4, lineHeight: 1, mb: 0.5}}>
                                {s.value}
                            </Typography>
                            <Typography sx={{fontSize: 12.5, fontWeight: 600, color: Grey6, textTransform: "uppercase", letterSpacing: "0.5px"}}>
                                {s.label}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                {/* Category-grouped tiles */}
                {([
                    {
                        category: "Identity & Access",
                        items: [
                            {kind: "wordmark", name: "Microsoft Entra ID", note: "SSO & SCIM provisioning", brand: "#0078D4", short: "MS"},
                            {kind: "wordmark", name: "Okta", note: "SSO & MFA", brand: "#007DC1", short: "OK"},
                            {kind: "wordmark", name: "Google Workspace", note: "SSO & Drive sync", brand: "#4285F4", short: "G"},
                            {kind: "wordmark", name: "OneLogin", note: "SSO & directory sync", brand: "#1C1F2A", short: "1L"},
                        ],
                    },
                    {
                        category: "Productivity & Documents",
                        items: [
                            {kind: "wordmark", name: "OneDrive", note: "Controlled document import", brand: "#0364B8", short: "OD"},
                            {kind: "wordmark", name: "SharePoint", note: "Document library sync", brand: "#0F6CBD", short: "SP"},
                            {kind: "wordmark", name: "Google Drive", note: "Live doc bidirectional sync", brand: "#34A853", short: "GD"},
                            {kind: "wordmark", name: "Confluence", note: "Wiki & knowledge linking", brand: "#172B4D", short: "Cf"},
                        ],
                    },
                    {
                        category: "Tickets & Engineering Change",
                        items: [
                            {kind: "wordmark", name: "Jira", note: "Engineering change tracking", brand: "#0052CC", short: "JR"},
                            {kind: "wordmark", name: "Linear", note: "Issue & change sync", brand: "#5E6AD2", short: "LN"},
                            {kind: "wordmark", name: "ServiceNow", note: "ITSM & CMDB sync", brand: "#62D84E", short: "SN"},
                            {kind: "wordmark", name: "GitHub", note: "Source-of-truth code linking", brand: "#1B1F23", short: "GH"},
                        ],
                    },
                    {
                        category: "CAD & PLM",
                        items: [
                            {kind: "wordmark", name: "Autodesk Fusion", note: "Parametric CAD/CAM", brand: "#0696D7", short: "AF"},
                            {kind: "wordmark", name: "PTC Windchill", note: "Enterprise PLM", brand: "#5C3D9C", short: "PT"},
                        ],
                    },
                    {
                        category: "ERP & Finance",
                        items: [
                            {kind: "image", img: billLogo, alt: "Bill.com", name: "Bill.com", note: "AP automation & payments", brand: "#D5202A"},
                            {kind: "wordmark", name: "NetSuite", note: "Cloud ERP & financials", brand: "#1C355E", short: "NS"},
                            {kind: "wordmark", name: "SAP", note: "Enterprise ERP", brand: "#0FAAFF", short: "SAP"},
                            {kind: "wordmark", name: "QuickBooks", note: "Small-business accounting", brand: "#2CA01C", short: "qb"},
                        ],
                    },
                    {
                        category: "Signatures & Forms",
                        items: [
                            {kind: "wordmark", name: "DocuSign", note: "External e-signatures", brand: "#FFCC22", short: "DS"},
                            {kind: "wordmark", name: "Adobe Sign", note: "Adobe ecosystem signing", brand: "#FA0F00", short: "AS"},
                            {kind: "wordmark", name: "HelloSign", note: "Quick external signing", brand: "#0061FF", short: "HS"},
                            {kind: "wordmark", name: "Formstack", note: "Compliant form intake", brand: "#21B573", short: "FS"},
                        ],
                    },
                ] as {category: string; items: ({kind: "wordmark"; name: string; note: string; brand: string; short: string} | {kind: "image"; img: string; alt: string; name: string; note: string; brand: string})[]}[]).map((grp, gi) => (
                    <Box key={gi} sx={{maxWidth: 1280, mx: "auto", mb: gi === 5 ? 0 : 5}}>
                        <Box sx={{display: "flex", alignItems: "center", gap: 1.5, mb: 2}}>
                            <Box sx={{width: 6, height: 22, borderRadius: "3px", backgroundColor: Blue3}}/>
                            <Typography sx={{fontSize: 16, fontWeight: 700, color: Grey17, textTransform: "uppercase", letterSpacing: "1px"}}>
                                {grp.category}
                            </Typography>
                        </Box>
                        <Box sx={{
                            display: "grid",
                            gridTemplateColumns: {xs: "1fr 1fr", sm: "repeat(4, 1fr)"},
                            gap: 2.5,
                        }}>
                            {grp.items.map((it, i) => (
                                <Box key={i} sx={{
                                    backgroundColor: White,
                                    border: `1px solid ${Grey2}`,
                                    borderRadius: "12px",
                                    p: 2.5,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    textAlign: "center",
                                    transition: "transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease",
                                    "&:hover": {transform: "translateY(-3px)", boxShadow: "0 10px 24px rgba(0,0,0,0.06)", borderColor: it.brand},
                                }}>
                                    <Box sx={{
                                        width: 72, height: 72, borderRadius: "12px",
                                        backgroundColor: it.kind === "wordmark" ? it.brand : Grey1b,
                                        display: "flex", alignItems: "center", justifyContent: "center", mb: 1.5,
                                        overflow: "hidden",
                                    }}>
                                        {it.kind === "image" ? (
                                            <img src={it.img} alt={it.alt}
                                                style={{maxWidth: "72%", maxHeight: "72%", objectFit: "contain"}}/>
                                        ) : (
                                            <Typography sx={{
                                                color: White,
                                                fontWeight: 800,
                                                fontSize: it.short.length <= 2 ? 28 : 20,
                                                letterSpacing: "-0.5px",
                                                fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
                                            }}>
                                                {it.short}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Typography sx={{fontSize: 14.5, fontWeight: 600, color: Grey17, mb: 0.25}}>
                                        {it.name}
                                    </Typography>
                                    <Typography sx={{fontSize: 12, color: Grey6, lineHeight: 1.4}}>
                                        {it.note}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                ))}

                {/* Closing line */}
                <Box sx={{
                    maxWidth: 1280, mx: "auto", mt: 6,
                    display: "flex", alignItems: "center", gap: 2,
                    backgroundColor: White,
                    border: `1px solid ${Grey2}`,
                    borderLeft: `4px solid ${Blue3}`,
                    borderRadius: "10px",
                    p: 3,
                    flexWrap: "wrap",
                }}>
                    <AccountBalanceOutlined sx={{fontSize: 28, color: Blue4}}/>
                    <Typography sx={{flex: 1, fontSize: 14.5, color: Grey7, lineHeight: 1.55, minWidth: 240}}>
                        Don't see what you need? Every record in the platform &mdash; documents, training, events,
                        CAPAs, suppliers, design files &mdash; is exposed through a documented, versioned REST API
                        and a signed-webhook event stream. Roll your own integration and publish it as a
                        reusable template.
                    </Typography>
                </Box>
            </Box>

            {/* ───────────── Always-on regulatory intelligence (Agentic AI) ───────────── */}
            <Box sx={{
                position: "relative",
                px: {xs: 3, md: 10}, py: {xs: 8, md: 12},
                background: "radial-gradient(ellipse at top, #1a1f3a 0%, #0b0d1f 60%, #06070f 100%)",
                color: White,
                overflow: "hidden",
            }}>
                <Box sx={{
                    position: "absolute", inset: 0, opacity: 0.4,
                    backgroundImage: "radial-gradient(circle at 15% 20%, rgba(124, 58, 237, 0.5) 1px, transparent 1.5px), radial-gradient(circle at 80% 60%, rgba(20, 152, 216, 0.5) 1px, transparent 1.5px), radial-gradient(circle at 45% 85%, rgba(255,255,255,0.4) 1px, transparent 1.5px), radial-gradient(circle at 90% 15%, rgba(255,255,255,0.3) 1px, transparent 1.5px)",
                    backgroundSize: "240px 240px, 320px 320px, 180px 180px, 280px 280px",
                    pointerEvents: "none",
                }}/>

                <Box sx={{position: "relative", maxWidth: 1280, mx: "auto"}}>
                    <Box sx={{textAlign: "center", mb: {xs: 6, md: 8}, maxWidth: 820, mx: "auto"}}>
                        <Box sx={{
                            display: "inline-flex", alignItems: "center", gap: 1,
                            background: "linear-gradient(90deg, rgba(124, 58, 237, 0.2), rgba(20, 152, 216, 0.2))",
                            border: "1px solid rgba(124, 58, 237, 0.4)",
                            borderRadius: "999px", px: 2, py: 0.75, mb: 3,
                        }}>
                            <AutoAwesome sx={{fontSize: 16, color: "#c4b5fd"}}/>
                            <Typography sx={{fontSize: 13, fontWeight: 600, color: "#e9d5ff", letterSpacing: "0.5px", textTransform: "uppercase"}}>
                                Always-on regulatory intelligence
                            </Typography>
                        </Box>
                        <Typography sx={{fontSize: {xs: 32, md: 52}, fontWeight: 700, lineHeight: 1.1, mb: 2.5,
                            background: "linear-gradient(90deg, #ffffff 0%, #c4b5fd 50%, #67e8f9 100%)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                        }}>
                            Regulatory agents that never sleep
                        </Typography>
                        <Typography sx={{fontSize: {xs: 16, md: 19}, color: "rgba(255,255,255,0.78)", lineHeight: 1.6, maxWidth: 740, mx: "auto"}}>
                            Background agents monitor FDA, ISO, and EU guidance feeds, cross-reference
                            them against your live procedures and design files, and surface impact
                            assessments — before the next audit, not after.
                        </Typography>
                    </Box>

                    {/* Agent canvas mockup */}
                    <Box sx={{
                        position: "relative",
                        borderRadius: "20px",
                        border: "1px solid rgba(255,255,255,0.08)",
                        background: "linear-gradient(135deg, rgba(20, 24, 50, 0.95) 0%, rgba(15, 18, 38, 0.95) 100%)",
                        backdropFilter: "blur(20px)",
                        boxShadow: "0 30px 80px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255,255,255,0.05)",
                        mb: {xs: 8, md: 10},
                        overflow: "hidden",
                    }}>
                        <Box sx={{
                            display: "flex", alignItems: "center", gap: 1.5,
                            px: 2.5, py: 1.5,
                            borderBottom: "1px solid rgba(255,255,255,0.06)",
                            backgroundColor: "rgba(0,0,0,0.3)",
                        }}>
                            {["#FF5F56", "#FFBD2E", "#27C93F"].map(c => (
                                <Box key={c} sx={{width: 11, height: 11, borderRadius: "50%", backgroundColor: c}}/>
                            ))}
                            <Box sx={{display: "flex", alignItems: "center", gap: 1, ml: 2}}>
                                <AccountTree sx={{fontSize: 16, color: "rgba(255,255,255,0.5)"}}/>
                                <Typography sx={{fontSize: 12.5, color: "rgba(255,255,255,0.6)", fontFamily: "monospace"}}>
                                    fda-guidance-watcher.flow
                                </Typography>
                            </Box>
                            <Box sx={{flex: 1}}/>
                            <Box sx={{
                                display: "flex", alignItems: "center", gap: 0.75,
                                backgroundColor: "rgba(16, 185, 129, 0.15)",
                                border: "1px solid rgba(16, 185, 129, 0.4)",
                                borderRadius: "6px", px: 1.25, py: 0.4,
                            }}>
                                <Box sx={{width: 6, height: 6, borderRadius: "50%", backgroundColor: "#10b981"}}/>
                                <Typography sx={{fontSize: 11, color: "#6ee7b7", fontWeight: 600}}>WATCHING</Typography>
                            </Box>
                        </Box>

                        <Box sx={{
                            position: "relative",
                            minHeight: {xs: 460, md: 380},
                            backgroundImage: "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
                            backgroundSize: "24px 24px",
                            p: {xs: 2, md: 4},
                        }}>
                            <svg
                                viewBox="0 0 1000 380"
                                preserveAspectRatio="none"
                                style={{position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none"}}
                            >
                                <defs>
                                    <linearGradient id="flowGradHome" x1="0%" y1="0%" x2="100%" y2="0%">
                                        <stop offset="0%" stopColor="#0ea5e9"/>
                                        <stop offset="50%" stopColor="#7c3aed"/>
                                        <stop offset="100%" stopColor="#22d3ee"/>
                                    </linearGradient>
                                </defs>
                                <path d="M 145 95 C 200 95, 220 95, 285 95" stroke="url(#flowGradHome)" strokeWidth="2" fill="none"/>
                                <path d="M 425 95 C 480 95, 500 95, 565 95" stroke="url(#flowGradHome)" strokeWidth="2" fill="none"/>
                                <path d="M 705 95 C 760 95, 820 130, 820 190" stroke="url(#flowGradHome)" strokeWidth="2" fill="none"/>
                                <path d="M 760 220 C 700 220, 620 250, 565 285" stroke="url(#flowGradHome)" strokeWidth="2" fill="none"/>
                                <path d="M 880 220 C 920 220, 920 285, 880 285" stroke="rgba(255,255,255,0.15)" strokeWidth="2" fill="none" strokeDasharray="4 4"/>
                                <path d="M 425 285 C 370 285, 340 285, 285 285" stroke="url(#flowGradHome)" strokeWidth="2" fill="none"/>
                                <path d="M 145 285 C 100 285, 80 245, 80 195" stroke="url(#flowGradHome)" strokeWidth="2" fill="none"/>
                            </svg>

                            {([
                                {top: "12%", left: "1%", icon: <NotificationsActive sx={{fontSize: 22, color: "#fff"}}/>, kind: "TRIGGER",
                                    title: "FDA guidance update", sub: "Source: fda.gov/medical-devices", accent: "#0ea5e9"},
                                {top: "12%", left: "26%", icon: <Psychology sx={{fontSize: 22, color: "#fff"}}/>, kind: "LLM",
                                    title: "Classify & extract", sub: "claude-opus-4 · structured impact", accent: "#7c3aed"},
                                {top: "12%", left: "55%", icon: <SearchOutlined sx={{fontSize: 22, color: "#fff"}}/>, kind: "TOOL",
                                    title: "Match controlled docs", sub: "SOPs, DHFs, risk files", accent: "#22d3ee"},
                                {top: "47%", left: "73%", icon: <Insights sx={{fontSize: 22, color: "#fff"}}/>, kind: "DECISION",
                                    title: "Impact score?", sub: "score ≥ 0.7 → escalate", accent: "#f59e0b"},
                                {top: "70%", left: "39%", icon: <HowToReg sx={{fontSize: 22, color: "#fff"}}/>, kind: "HUMAN",
                                    title: "RA lead reviews", sub: "21 CFR Part 11 signature", accent: "#ec4899"},
                                {top: "70%", left: "1%", icon: <CheckCircleOutlined sx={{fontSize: 22, color: "#fff"}}/>, kind: "ACTION",
                                    title: "Draft SOP revision", sub: "Routed for approval", accent: "#10b981"},
                            ] as {top: string; left: string; icon: React.ReactNode; kind: string; title: string; sub?: string; accent: string}[]).map((n, i) => (
                                <Box key={i} sx={{
                                    position: "absolute", top: n.top, left: n.left,
                                    width: {xs: 138, md: 160},
                                    backgroundColor: "rgba(20, 24, 50, 0.95)",
                                    border: `1px solid ${n.accent}55`,
                                    borderLeft: `3px solid ${n.accent}`,
                                    borderRadius: "10px",
                                    p: 1.5,
                                    boxShadow: `0 6px 18px rgba(0,0,0,0.4), 0 0 24px ${n.accent}20`,
                                    backdropFilter: "blur(10px)",
                                    zIndex: 2,
                                }}>
                                    <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 0.75}}>
                                        <Box sx={{
                                            width: 30, height: 30, borderRadius: "8px",
                                            backgroundColor: n.accent,
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                            flexShrink: 0,
                                        }}>
                                            {n.icon}
                                        </Box>
                                        <Typography sx={{fontSize: 10, fontWeight: 700, color: n.accent, letterSpacing: "0.5px"}}>
                                            {n.kind}
                                        </Typography>
                                    </Box>
                                    <Typography sx={{fontSize: 13, fontWeight: 600, color: "#fff", mb: 0.25, lineHeight: 1.25}}>
                                        {n.title}
                                    </Typography>
                                    {n.sub && (
                                        <Typography sx={{fontSize: 11, color: "rgba(255,255,255,0.5)", fontFamily: "monospace", lineHeight: 1.3}}>
                                            {n.sub}
                                        </Typography>
                                    )}
                                </Box>
                            ))}

                            <Box sx={{
                                position: "absolute", top: "8%", right: "1%",
                                width: {xs: 0, md: 200},
                                display: {xs: "none", md: "block"},
                                backgroundColor: "rgba(11, 13, 31, 0.85)",
                                border: "1px solid rgba(255,255,255,0.08)",
                                borderRadius: "10px",
                                p: 1.5,
                                fontFamily: "monospace",
                                fontSize: 10.5,
                                color: "rgba(255,255,255,0.65)",
                                zIndex: 1,
                            }}>
                                <Box sx={{display: "flex", alignItems: "center", gap: 0.75, mb: 1, color: "#a7f3d0"}}>
                                    <HistoryEdu sx={{fontSize: 14}}/>
                                    <span style={{fontWeight: 700, fontSize: 10, letterSpacing: "0.5px"}}>AUDIT TRAIL</span>
                                </Box>
                                {[
                                    "09:01:04 · feed polled",
                                    "09:01:05 · 1 new guidance",
                                    "09:01:06 · llm.tokens=684",
                                    "09:01:08 · matched 7 SOPs",
                                    "09:01:08 · impact=0.81",
                                    "09:01:09 · escalated → RA",
                                    "10:42:11 · approved by k.lee",
                                    "10:42:12 · SOP-114 rev. drafted",
                                ].map((l, i) => (
                                    <Box key={i} sx={{lineHeight: 1.6, opacity: 0.65 + i * 0.05, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                                        {l}
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Box>

                    {/* Three differentiators */}
                    <Box sx={{
                        display: "grid",
                        gridTemplateColumns: {xs: "1fr", md: "repeat(3, 1fr)"},
                        gap: 3,
                        mb: {xs: 8, md: 10},
                    }}>
                        {[
                            {icon: <Visibility sx={{fontSize: 28, color: "#67e8f9"}}/>, accent: "#22d3ee",
                                title: "Every decision auditable",
                                description: "Each node records its inputs, outputs, model, tokens, and tool calls. Replay any agent run — it's already in the inspection-ready audit trail."},
                            {icon: <Timeline sx={{fontSize: 28, color: "#c4b5fd"}}/>, accent: "#7c3aed",
                                title: "Mapped to your procedures",
                                description: "Agents traverse your live SOPs, design history files, and risk tables — recommendations cite the specific records they would change."},
                            {icon: <Lock sx={{fontSize: 28, color: "#6ee7b7"}}/>, accent: "#10b981",
                                title: "Compliance enforced at runtime",
                                description: "Approvals, electronic signatures, retention windows, and data-residency policies are enforced by the engine — not bolted on after the fact."},
                        ].map((d, i) => (
                            <Box key={i} sx={{
                                backgroundColor: "rgba(20, 24, 50, 0.7)",
                                border: `1px solid ${d.accent}30`,
                                borderTop: `2px solid ${d.accent}`,
                                borderRadius: "14px",
                                p: 3.5,
                                backdropFilter: "blur(10px)",
                            }}>
                                <Box sx={{
                                    width: 56, height: 56, borderRadius: "12px",
                                    backgroundColor: `${d.accent}1a`,
                                    border: `1px solid ${d.accent}40`,
                                    display: "flex", alignItems: "center", justifyContent: "center", mb: 2,
                                }}>
                                    {d.icon}
                                </Box>
                                <Typography sx={{fontSize: 18, fontWeight: 700, color: "#fff", mb: 1}}>
                                    {d.title}
                                </Typography>
                                <Typography sx={{fontSize: 14, color: "rgba(255,255,255,0.7)", lineHeight: 1.6}}>
                                    {d.description}
                                </Typography>
                            </Box>
                        ))}
                    </Box>

                    {/* Use case examples */}
                    <Box>
                        <Typography sx={{fontSize: 13, fontWeight: 600, color: "#a78bfa", letterSpacing: "1.5px", textTransform: "uppercase", textAlign: "center", mb: 1.5}}>
                            Real workflows our customers run
                        </Typography>
                        <Typography sx={{fontSize: {xs: 24, md: 30}, fontWeight: 700, color: "#fff", textAlign: "center", mb: 5, lineHeight: 1.25}}>
                            Agentic automations across the QMS
                        </Typography>

                        <Box sx={{
                            display: "grid",
                            gridTemplateColumns: {xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)"},
                            gap: 2.5,
                        }}>
                            {[
                                {tag: "Complaints", color: "#22d3ee",
                                    title: "Complaint triage agent",
                                    body: "Classify incoming complaints, draft an MDR-decision rationale, and route to the right investigator with the relevant prior CAPAs attached."},
                                {tag: "Suppliers", color: "#fbbf24",
                                    title: "Supplier risk monitor",
                                    body: "Watch external warning letters and recalls. When a supplier is named, queue a re-qualification task and flag affected lots and products."},
                                {tag: "Design Controls", color: "#a78bfa",
                                    title: "Change-impact analyst",
                                    body: "On every engineering change, walk the requirement trace, identify affected verification tests, and draft the impact summary for review."},
                                {tag: "Training", color: "#f472b6",
                                    title: "Re-training dispatcher",
                                    body: "When a controlled document is revised, identify every affected role, schedule re-training, and chase signatures until 100% completion."},
                            ].map((uc, i) => (
                                <Box key={i} sx={{
                                    backgroundColor: "rgba(20, 24, 50, 0.6)",
                                    border: `1px solid rgba(255,255,255,0.08)`,
                                    borderRadius: "12px",
                                    p: 3,
                                    transition: "transform 200ms ease, border-color 200ms ease, background-color 200ms ease",
                                    "&:hover": {transform: "translateY(-3px)", borderColor: `${uc.color}80`, backgroundColor: "rgba(20, 24, 50, 0.9)"},
                                }}>
                                    <Box sx={{
                                        display: "inline-block",
                                        backgroundColor: `${uc.color}20`,
                                        border: `1px solid ${uc.color}50`,
                                        borderRadius: "6px",
                                        px: 1, py: 0.25,
                                        fontSize: 11, fontWeight: 700, color: uc.color,
                                        letterSpacing: "0.5px",
                                        mb: 1.5,
                                    }}>
                                        {uc.tag}
                                    </Box>
                                    <Typography sx={{fontSize: 16, fontWeight: 600, color: "#fff", mb: 1, lineHeight: 1.3}}>
                                        {uc.title}
                                    </Typography>
                                    <Typography sx={{fontSize: 13.5, color: "rgba(255,255,255,0.65)", lineHeight: 1.55}}>
                                        {uc.body}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* ───────────── What an auditor is shown ─────────────

                THIS SECTION WAS THREE FABRICATED TESTIMONIALS. Invented quotes, invented metrics
                ("0 findings", "47 days", "1 inspection saved"), attributed to job titles at
                unnamed companies, under the heading "Trusted by quality teams" -- on a page
                anybody can read, for a product with no such customers.

                That is not a tone problem. A quote attributed to a named role at a described
                company is a claim about a real organisation, and the first place an invented one
                goes is into a sales deck, where nobody remembers it was a placeholder. The same
                three were removed from Administration's splash on 2026-10-11; these are their
                siblings.

                WHAT REPLACES THEM IS TRUE AND CHECKABLE. The same card layout, saying what the
                system does when somebody asks it to prove something -- each of which can be
                demonstrated in the product rather than quoted from somebody who does not exist.
                If social proof is wanted here, it needs a real customer willing to be named. */}
            <Box sx={{px: {xs: 4, md: 10}, py: {xs: 8, md: 10}, backgroundColor: White}}>
                <Box sx={{textAlign: "center", mb: 6, maxWidth: 760, mx: "auto"}}>
                    <Typography sx={{fontSize: 14, fontWeight: 600, color: Blue3, textTransform: "uppercase", letterSpacing: "1.5px", mb: 1.5}}>
                        When somebody asks you to prove it
                    </Typography>
                    <Typography sx={{fontSize: {xs: 28, md: 36}, fontWeight: 700, color: Grey17, lineHeight: 1.2}}>
                        Three questions an audit turns on, and where the answer lives
                    </Typography>
                </Box>

                <Box sx={{
                    display: "grid",
                    gridTemplateColumns: {xs: "1fr", md: "repeat(3, 1fr)"},
                    gap: 3,
                    maxWidth: 1280, mx: "auto",
                }}>
                    {[
                        {quote: "Every approval records the printed name of whoever signed, what their signature meant, and the fact that they re-entered their password at the moment of signing \u2014 21 CFR 11.50 and 11.200(a)(1), in the record rather than in a policy document.",
                            who: "Who approved this, and when", metric: "Part 11 signatures"},
                        {quote: "A released revision is frozen and a change makes a new one, so \u201cwhat did this procedure say when that batch shipped\u201d is a question the system answers rather than one somebody reconstructs from a shared drive.",
                            who: "What did it say at the time", metric: "Revision history"},
                        {quote: "A signature is bound by digest to the thing it signed. Change the record after it was submitted and signing is refused by name \u2014 because a signature on a document that has moved no longer describes anything.",
                            who: "How do you know it has not changed", metric: "Checkable, not asserted"},
                    ].map((t, i) => (
                        <Box key={i} sx={{
                            backgroundColor: Grey1b,
                            border: `1px solid ${Grey2}`,
                            borderRadius: "14px",
                            p: 3.5,
                            display: "flex", flexDirection: "column", gap: 2,
                        }}>
                            <Box sx={{
                                display: "inline-flex", alignItems: "center", gap: 1,
                                backgroundColor: "rgba(0,160,68,0.10)",
                                border: `1px solid rgba(0,160,68,0.30)`,
                                borderRadius: "999px",
                                px: 1.5, py: 0.5,
                                alignSelf: "flex-start",
                            }}>
                                <AssignmentTurnedInOutlined sx={{fontSize: 14, color: Green4}}/>
                                <Typography sx={{fontSize: 12, fontWeight: 700, color: Green4, letterSpacing: "0.5px"}}>
                                    {t.metric}
                                </Typography>
                            </Box>
                            <Typography sx={{fontSize: 15.5, color: Grey17, lineHeight: 1.6}}>
                                {t.quote}
                            </Typography>
                            {/* THE QUESTION, NOT AN ATTRIBUTION. There is nobody to attribute
                                this to, and inventing one is what this section used to do. */}
                            <Typography sx={{fontSize: 13, fontWeight: 600, color: Grey7}}>
                                {t.who}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* ───────────── Final CTA ───────────── */}
            <Box sx={{
                px: {xs: 4, md: 10}, py: {xs: 8, md: 10},
                background: `linear-gradient(135deg, ${Blue4} 0%, ${Blue3} 100%)`,
                color: White,
                textAlign: "center",
            }}>
                <Typography sx={{fontSize: {xs: 28, md: 38}, fontWeight: 700, mb: 2, lineHeight: 1.2}}>
                    Get audit-ready in 60 days
                </Typography>
                <Typography sx={{fontSize: 17, color: "rgba(255,255,255,0.92)", mb: 4, maxWidth: 640, mx: "auto"}}>
                    Sign in to your tenant, or talk to us about standing up a fully validated
                    QMS configuration for your medical device, pharma, or biotech program.
                </Typography>
                <Box sx={{display: "flex", justifyContent: "center", gap: 2, flexWrap: "wrap"}}>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={openLogin}
                        endIcon={<ArrowForward/>}
                        sx={{
                            backgroundColor: White, color: Blue4, fontWeight: 600, px: 4, py: 1.4,
                            textTransform: "none", fontSize: 16,
                            "&:hover": {backgroundColor: Blue1, color: Blue5},
                        }}
                    >
                        Sign in
                    </Button>
                    <Button
                        variant="outlined"
                        size="large"
                        onClick={openLogin}
                        startIcon={<FactCheckOutlined/>}
                        sx={{
                            color: White, borderColor: "rgba(255,255,255,0.6)",
                            fontWeight: 500, px: 4, py: 1.4, textTransform: "none", fontSize: 16,
                            "&:hover": {borderColor: White, backgroundColor: "rgba(255,255,255,0.08)"},
                        }}
                    >
                        Request a demo
                    </Button>
                </Box>
            </Box>

            {/* ───────────── Mini footer ───────────── */}
            <Box sx={{px: {xs: 4, md: 10}, py: 4, backgroundColor: Grey17, color: Grey5, textAlign: "center", fontSize: 13}}>
                <Typography sx={{fontSize: 13}}>
                    &copy; {new Date().getFullYear()} Parkour AI &mdash; Quality Management System for FDA-regulated life sciences.
                </Typography>
            </Box>
        </div>
    );
}
